import { useState, useRef, useEffect } from "react";
import { EditIcon, Image, Palette, TrashIcon } from "lucide-react";
import ModalBare from "../../../../components/ModalBare";
import useModal from "../../../../hooks/useModal";
import { Button } from "@oc-labs/wt-storybook";

const PRESET_COLORS = [
  "#3B82F6", "#EF4444", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899",
  "#06B6D4", "#84CC16", "#F97316", "#6B7280", "#1F2937", "#000000",
];

export default function BackgroundEditModal({ company, onSave }) {
  const modal = useModal("editBackground");
  const [activeTab, setActiveTab] = useState("image");
  const [image, setImage] = useState(company?.backgroundImage || "");
  const [backgroundColor, setBackgroundColor] = useState(company?.backgroundColor || "#3B82F6");
  const [customColor, setCustomColor] = useState(company?.backgroundColor || "#3B82F6");
  const [iframeBackgroundColor, setIframeBackgroundColor] = useState(company?.iframeBackgroundColor || "#FFFFFF");
  const [customIframeColor, setCustomIframeColor] = useState(company?.iframeBackgroundColor || "#FFFFFF");
  const [backgroundImageOpacity, setBackgroundImageOpacity] = useState(company?.backgroundImageOpacity ?? 1);

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (modal.isOpen) {
      setImage(company?.backgroundImage || "");
      setBackgroundColor(company?.backgroundColor || "#3B82F6");
      setCustomColor(company?.backgroundColor || "#3B82F6");
      setIframeBackgroundColor(company?.iframeBackgroundColor || "#FFFFFF");
      setCustomIframeColor(company?.iframeBackgroundColor || "#FFFFFF");
      setBackgroundImageOpacity(company?.backgroundImageOpacity ?? 1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal.isOpen]);

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result);
    
        if (fileInputRef.current) fileInputRef.current.value = "";
      };
      reader.readAsDataURL(file);
    } else {

      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleEditClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveImage = () => {
    setImage("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleColorSelect = (color) => {
    setBackgroundColor(color);
    setCustomColor(color);
  };

  const handleCustomColorChange = (e) => {
    const value = e.target.value;
    setCustomColor(value);
    if (/^#[0-9A-F]{6}$/i.test(value)) {
      setBackgroundColor(value);
    }
  };

  const handleSave = () => {
    const updateData = {};
    if (activeTab === "image") {
      updateData.backgroundImage = image || "";
      updateData.backgroundImageOpacity = Math.max(0, Math.min(1, Number(backgroundImageOpacity) || 1));
    } else {
      updateData.backgroundColor = backgroundColor;
      updateData.iframeBackgroundColor = iframeBackgroundColor;
    }
    onSave?.(updateData);
    modal.close();
  };

  return (
    <ModalBare name="editBackground" title="Background Settings" className="min-w-lg">
      <p className="text-sm text-gray-600 px-6 py-2">Configure the wallet and iframe screen background style</p>
      
      <div className="h-[30vh] overflow-y-auto flex flex-col">
      {activeTab === "image" && (
        <div className="w-full h-full overflow-y-auto">
          {image ? (
            <div className="flex flex-col items-center justify-center h-64 bg-gray-100  w-full py-30">
              <img src={image} alt="Background" className="object-cover h-48 rounded-lg mb-4" />
              <div className="flex gap-4">
                <Button size="small" type="secondary" onClick={handleEditClick}>
                  <EditIcon className="w-4 h-4" />
                </Button>
                <Button size="small" type="secondary" onClick={handleRemoveImage}>
                  <TrashIcon className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ) : (
            <div
              className="bg-gray-100 w-full flex flex-col items-center justify-center text-center cursor-pointer hover:bg-gray-200 transition py-20"
              onClick={handleEditClick}
            >
              <p className="text-gray-600 font-medium mb-4">Drag and drop image file</p>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditClick();
                }}
                className="cursor-pointer ml-4 px-3 py-1 rounded-xl bg-white text-gray-900 text-md hover:bg-gray-50 transition-colors"
              >
                Upload file
              </button>
            </div>
          )}
          {image && (
            <div className="mt-4 space-y-2 px-6">
              <label className="text-sm font-medium text-gray-700">Background Opacity</label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={backgroundImageOpacity}
                  onChange={(e) => setBackgroundImageOpacity(parseFloat(e.target.value))}
                  className="flex-1"
                />
                <span className="text-sm font-medium w-10 text-right">{Math.round(backgroundImageOpacity * 100)}%</span>
              </div>
            </div>
          )}
        </div>
      )}
      {activeTab === "color" && (
        <div className="w-full h-full overflow-y-auto">
            {/* Dos columnas para las secciones */}
            <div className="grid grid-cols-2 gap-8 px-8 pt-4">
              {/* Wallet Background Color Section */}
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-3">Wallet Background</h4>
                <div className="w-full h-20 rounded-lg mb-4 border border-gray-200" style={{ backgroundColor: customColor && customColor.length >= 4 ? customColor : '#3B82F6' }} />
          
                <label className="text-xs font-medium text-gray-700">Preset Colors</label>
                <div className="grid grid-cols-6 gap-2 mb-4 mt-2">
                  {PRESET_COLORS.map((color) => (
                    <button
                      key={color}
                      onClick={() => handleColorSelect(color)}
                      className={`w-full aspect-square rounded-lg transition ${
                        backgroundColor === color
                          ? "ring-2 ring-blue-600 ring-offset-2"
                          : "hover:ring-2 hover:ring-gray-300"
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
               
                <div>
                  <label className="text-xs font-medium text-gray-700">Custom Color</label>
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="color"
                      value={customColor}
                      onChange={handleCustomColorChange}
                      className="w-10 h-8 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={customColor || backgroundColor}
                      onChange={handleCustomColorChange}
                      placeholder="#RRGGBB"
                      className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs"
                    />
                  </div>
                </div>
              </div>

              {/* Iframe Background Color Section */}
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-3">Iframe Background</h4>
                <div className="w-full h-20 rounded-lg mb-4 border border-gray-200" style={{ backgroundColor: customIframeColor && customIframeColor.length >= 4 ? customIframeColor : '#FFFFFF' }} />
          
                <label className="text-xs font-medium text-gray-700">Preset Colors</label>
                <div className="grid grid-cols-6 gap-2 mb-4 mt-2">
                  {PRESET_COLORS.map((color) => (
                    <button
                      key={color}
                      onClick={() => {
                        setIframeBackgroundColor(color);
                        setCustomIframeColor(color);
                      }}
                      className={`w-full aspect-square rounded-lg transition ${
                        iframeBackgroundColor === color
                          ? "ring-2 ring-blue-600 ring-offset-2"
                          : "hover:ring-2 hover:ring-gray-300"
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
               
                <div>
                  <label className="text-xs font-medium text-gray-700">Custom Color</label>
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="color"
                      value={customIframeColor}
                      onChange={(e) => {
                        const value = e.target.value;
                        setCustomIframeColor(value);
                        if (/^#[0-9A-F]{6}$/i.test(value)) {
                          setIframeBackgroundColor(value);
                        }
                      }}
                      className="w-10 h-8 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={customIframeColor || iframeBackgroundColor}
                      onChange={(e) => {
                        const value = e.target.value;
                        setCustomIframeColor(value);
                        if (/^#[0-9A-F]{6}$/i.test(value)) {
                          setIframeBackgroundColor(value);
                        }
                      }}
                      placeholder="#RRGGBB"
                      className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs"
                    />
                  </div>
                </div>
              </div>
            </div>
        </div>
      )}
      </div>

      <div className="flex justify-center mt-4 mb-4">
        <div className="flex gap-2 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('image')}
            className={`flex items-center gap-1 px-10 py-2 font-medium text-sm rounded-lg transition focus:outline-none cursor-pointer ${
              activeTab === 'image'
                ? 'bg-white text-blue-600'
                : 'bg-transparent text-gray-600 hover:text-blue-600'
            }`}
          >
            <Image className="h-4 w-4" /> Image
          </button>
          <button
            onClick={() => setActiveTab('color')}
            className={`flex items-center gap-1 px-10 py-2 font-medium text-sm rounded-lg transition  focus:outline-none cursor-pointer ${
              activeTab === 'color'
                ? 'bg-white text-blue-600'
                : 'text-gray-600 hover:text-blue-600'
            }`}
          >
            <Palette className="h-4 w-4" /> Color
          </button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
      />
  
      <div className="flex justify-center mt-6 mb-6 gap-4 px-6">
        <button
          type="button"
          onClick={() => {
            setBackgroundColor('#3B82F6');
            setCustomColor('#3B82F6');
            setIframeBackgroundColor('#FFFFFF');
            setCustomIframeColor('#FFFFFF');
            setImage(null);
            setBackgroundImageOpacity(1);
            if (fileInputRef.current) fileInputRef.current.value = "";
          }}
          className="px-8 py-2 rounded-3xl bg-gray-200 text-black font-medium hover:bg-gray-300 transition whitespace-nowrap cursor-pointer"
        >
          Reset to Default
        </button>
        <Button type="primary" onClick={handleSave} className="px-8">
          Save Changes
        </Button>
      </div>
    </ModalBare>
  );
}
